
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { shipmentStatusService } from '@/services/shipmentStatusService';
import { shipmentService } from '@/services/shipmentService';
import { useAuth } from '@/hooks/useAuth';
import { ExportButton } from '@/components/ExportButton';
import { 
  Package, Plus, Search, Eye, Edit, Trash2,
  ChevronLeft, ChevronRight, Truck, MapPin, Phone,
  CheckCircle2, Clock, XCircle, Filter
, Calendar } from 'lucide-react';

interface Shipment {
  id: number;
  trackingNumber: string;

  // الراسل
  senderName: string;
  senderPhone?: string;
  senderAddress?: string;
  senderCity?: string;

  // المرسل إليه
  receiverName: string;
  receiverPhone?: string;
  receiverCity?: string;
  receiverArea?: string;
  receiverAddress?: string;
  receiverLandmark?: string;
  receiverBuildingNumber?: string;
  receiverFloor?: string;
  receiverApartment?: string;

  // الشحنة
  weight: number;
  pieces: number;
  contentType?: string;
  description?: string;
  packageDescription?: string;

  // المبالغ
  shippingCost: number;
  totalAmount: number;
  codAmount: number;

  // الحالة الرئيسية
  statusId: number;
  statusName: string;

// حالة الموافقة الإدارية
  isApproved?: boolean;

  // الحالة الفرعية
  subStatusId?: number;
  subStatusName?: string;
  subStatusNameAr?: string;
  subStatusColor?: string;

  // الفرع
  branchId?: number;
  branchName?: string;
  branchPhone?: string;

  // المندوب
  delegateId?: number;
  delegateName?: string;
  delegatePhone?: string;

  // معلومات إضافية
  deliveryNotes?: string;
  createdAt: string;
}

const statusStyles: Record<number, { color: string; icon: any; label: string }> = {
  1: { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock, label: 'قيد الانتظار' },
  2: { color: 'bg-cyan-50 text-cyan-700 border-cyan-200', icon: Package, label: 'تم الاستلام' },
  3: { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Truck, label: 'في الطريق' },
  4: { color: 'bg-purple-50 text-purple-700 border-purple-200', icon: MapPin, label: 'جاري التوصيل' },
  5: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'تم التسليم' },
  6: { color: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle, label: 'مرتجع' },
  7: { color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle, label: 'ملغي' },
  8: { color: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: Package, label: 'في المخزن' },
  10: { color: 'bg-orange-50 text-orange-700 border-orange-200', icon: Truck, label: 'قيد التحويل' },
  11: { color: 'bg-teal-50 text-teal-700 border-teal-200', icon: MapPin, label: 'وصلت للفرع' },
};

export function ShipmentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
const [selectedShipmentIds, setSelectedShipmentIds] = useState<number[]>([]);
  const [shipmentStatuses, setShipmentStatuses] = useState<any[]>([]);
  const [bulkStatusId, setBulkStatusId] = useState('');
  const [bulkSubStatusId, setBulkSubStatusId] = useState('');
  const [subStatuses, setSubStatuses] = useState<any[]>([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
const exportColumns = [
    { key: 'trackingNumber', label: 'رقم التتبع' },
    { key: 'senderName', label: 'المرسل' },
    { key: 'receiverName', label: 'المستلم' },
    { key: 'receiverCity', label: 'المدينة' },
    { key: 'statusName', label: 'الحالة' },
    { key: 'codAmount', label: 'COD', format: (v: any) => v ?? 0 },
    { key: 'totalAmount', label: 'الإجمالي', format: (v: any) => v ?? 0 },
    { key: 'createdAt', label: 'التاريخ', format: (v: any) => v ? new Date(v).toLocaleDateString('ar-EG') : '' }
  ];

useEffect(() => {
  loadShipments();
}, [page, pageSize, statusFilter, search, dateFrom, dateTo]);

useEffect(() => {
  const loadShipmentStatuses = async () => {
    try {
      const response = await shipmentStatusService.getAll();

      if (response?.success) {
        setShipmentStatuses(response.data || []);
      }
    } catch (error) {
      console.error('Error loading shipment statuses:', error);
    }
  };

  loadShipmentStatuses();
}, []);

  useEffect(() => {
    const loadSubStatuses = async () => {
      if (!bulkStatusId) {
        setSubStatuses([]);
        setBulkSubStatusId('');
        return;
      }
      try {
        const response = await api.get(`/api/shipment-sub-statuses?statusId=${bulkStatusId}`);
        setSubStatuses(Array.isArray(response.data?.data) ? response.data.data : []);
        setBulkSubStatusId('');
      } catch (error) {
        console.error('Error loading shipment sub-statuses:', error);
        setSubStatuses([]);
        setBulkSubStatusId('');
      }
    };
    loadSubStatuses();
  }, [bulkStatusId]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        pageNumber: page.toString(),
        pageSize: pageSize.toString(),
      });
      
if (statusFilter) params.append('statusId', statusFilter);
if (search) params.append('search', search);
if (dateFrom) params.append('fromDate', dateFrom);
if (dateTo) params.append('toDate', dateTo);

      console.log('[DATE FILTER DEBUG]', {
        dateFrom,
        dateTo,
        requestUrl: `/api/shipments?${params.toString()}`
      });

      const role = user?.role?.toLowerCase();
      if (role === 'merchant' || role === 'تاجر') {
        params.append('userId', String(user?.id || ''));
      }
      if (role === 'delegate' || role === 'مندوب') {
        params.append('delegateId', String(user?.delegateId || user?.id || ''));
      }

      const response = await api.get(`/api/shipments?${params.toString()}`);
      if (response.data.success) {
        setShipments(response.data.data.items || []);
        const totalCount = response.data.data.totalCount || 0;
        const pageSize = response.data.data.pageSize || 20;
        setTotalPages(Math.ceil(totalCount / pageSize));
        setTotalItems(response.data.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Error loading shipments:', error);
    } finally {
      setLoading(false);
    }
  };
const toggleShipmentSelection = (id: number) => {
  setSelectedShipmentIds(prev =>
    prev.includes(id)
      ? prev.filter(shipmentId => shipmentId !== id)
      : [...prev, id]
  );
};

const toggleSelectAll = () => {
  const currentPageIds = shipments.map(shipment => shipment.id);

  const allSelected = currentPageIds.every(id =>
    selectedShipmentIds.includes(id)
  );

  if (allSelected) {
    setSelectedShipmentIds(prev =>
      prev.filter(id => !currentPageIds.includes(id))
    );
  } else {
    setSelectedShipmentIds(prev =>
      Array.from(new Set([...prev, ...currentPageIds]))
    );
  }
};

const handleBulkStatusUpdate = async () => {
  if (selectedShipmentIds.length === 0 || !bulkStatusId) return;

  if (!window.confirm(`هل أنت متأكد من تغيير حالة ${selectedShipmentIds.length} شحنة؟`)) {
    return;
  }

  try {
    setBulkUpdating(true);

    await shipmentService.bulkUpdateStatus(
      selectedShipmentIds,
      Number(bulkStatusId),
      bulkSubStatusId ? Number(bulkSubStatusId) : undefined
    );

    setSelectedShipmentIds([]);
    setBulkStatusId('');
    setBulkSubStatusId('');
    setSubStatuses([]);
    await loadShipments();

    alert('تم تحديث حالات الشحنات بنجاح');
  } catch (error) {
    console.error('Error updating shipment statuses:', error);
    alert('حدث خطأ أثناء تحديث حالات الشحنات');
  } finally {
    setBulkUpdating(false);
  }
};
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الشحنة؟ لا يمكن التراجع عن هذا الإجراء.')) return;
    try {
      await api.delete(`/api/shipments/${id}`);
      loadShipments();
    } catch (error) {
      console.error('Error deleting shipment:', error);
      alert('حدث خطأ أثناء الحذف');
    }
  };

  const canManage = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'merchant' || user?.role === 'تاجر';

  
  


  

return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20">
              <Package className="w-5 h-5 text-white" />
            </div>
            إدارة الشحنات
          </h1>
          <p className="text-gray-500 text-sm mt-1 mr-13">إجمالي {totalItems} شحنة في النظام</p>
        </div>
        
        {canManage && (
          <button
            onClick={() => navigate('/shipments/create')}
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-xl font-medium transition-all shadow-md shadow-orange-500/20"
          >
            <Plus className="w-4 h-4" />
            إنشاء شحنة جديدة
          </button>
        )}
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="بحث برقم التتبع، اسم المرسل أو المستلم..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pr-10 pl-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
            />
          </div>
          <div className="relative min-w-[180px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1 mr-1">
              من تاريخ
            </label>
            <div className="relative">
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="w-full pr-10 pl-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                title="من تاريخ"
              />
            </div>
          </div>

          <div className="relative min-w-[180px]">
            <label className="block text-xs font-semibold text-gray-500 mb-1 mr-1">
              إلى تاريخ
            </label>
            <div className="relative">
              <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className="w-full pr-10 pl-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                title="إلى تاريخ"
              />
            </div>
          </div>

          <div className="relative min-w-[180px]">
            <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full pr-10 pl-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white appearance-none"
            >
<option value="">كل الحالات</option>

{shipmentStatuses
  .filter((status) => status.isActive !== false)
  .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
  .map((status) => (
    <option key={status.id} value={status.id}>
      {status.nameAr || status.name}
    </option>
  ))}
            </select>
          </div>

          <button 
            type="submit" 
            className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2.5 rounded-xl text-sm font-medium transition-colors"
          >
            بحث
          </button>

          <ExportButton 
            data={shipments} 
            columns={exportColumns} 
            filename={`shipments_export_${new Date().toISOString().split('T')[0]}`}
            title="الشحنات"
          />
        </form>
      </div>

      {selectedShipmentIds.length > 0 && (
  <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 mb-4 flex flex-col sm:flex-row items-center gap-3">
    <div className="font-bold text-orange-800">
      تم تحديد {selectedShipmentIds.length} شحنة
    </div>

    <select
      value={bulkStatusId}
      onChange={(e) => setBulkStatusId(e.target.value)}
      className="w-full sm:w-64 px-4 py-2.5 border border-orange-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20"
      disabled={bulkUpdating}
    >
      <option value="">اختر الحالة الجديدة</option>
{shipmentStatuses
  .filter((status) => status.isActive !== false)
  .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
  .map((status) => (
    <option key={status.id} value={status.id}>
      {status.nameAr || status.name}
    </option>
  ))}
    </select>

    {subStatuses.length > 0 && (
      <select
        value={bulkSubStatusId}
        onChange={(e) => setBulkSubStatusId(e.target.value)}
        className="w-full sm:w-64 px-4 py-2.5 border border-orange-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20"
        disabled={bulkUpdating}
      >
        <option value="">اختر الحالة الفرعية اختيارياً</option>
        {subStatuses.map((subStatus) => (
          <option key={subStatus.id} value={subStatus.id}>
            {subStatus.nameAr || subStatus.name}
          </option>
        ))}
      </select>
    )}

    <button
      type="button"
      onClick={handleBulkStatusUpdate}
      disabled={!bulkStatusId || bulkUpdating}
      className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-xl text-sm font-bold transition-colors"
    >
      {bulkUpdating ? 'جاري التحديث...' : 'تحديث الحالة'}
    </button>

    <button
      type="button"
      onClick={() => {
        setSelectedShipmentIds([]);
        setBulkStatusId('');
      }}
      disabled={bulkUpdating}
      className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
    >
      إلغاء التحديد
    </button>
  </div>
)}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-3"></div>
            جاري تحميل الشحنات...
          </div>
        ) : shipments.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-medium">لا توجد شحنات مطابقة لبحثك</p>
            <p className="text-sm text-gray-400 mt-1">جرب تغيير معايير البحث أو الفلاتر</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-right">
<thead className="bg-gray-50/80 text-gray-600 font-semibold border-b border-gray-100">
  <tr>
    <th className="px-4 py-4 text-center whitespace-nowrap">
      <input
        type="checkbox"
        checked={
          shipments.length > 0 &&
          shipments.every(shipment =>
            selectedShipmentIds.includes(shipment.id)
          )
        }
        onChange={toggleSelectAll}
        className="w-4 h-4 accent-orange-600 cursor-pointer"
      />
    </th>

    <th className="px-6 py-4 whitespace-nowrap">رقم التتبع</th>
    <th className="px-6 py-4 whitespace-nowrap">تاريخ الإنشاء</th>

    <th className="px-6 py-4 whitespace-nowrap">الراسل</th>
    <th className="px-6 py-4 whitespace-nowrap">محافظة الراسل</th>

    <th className="px-6 py-4 whitespace-nowrap">المرسل إليه</th>
    <th className="px-6 py-4 whitespace-nowrap">محافظة المرسل إليه</th>
    <th className="px-6 py-4 whitespace-nowrap">منطقة المرسل إليه</th>
    <th className="px-6 py-4 whitespace-nowrap">العنوان التفصيلي</th>

    <th className="px-6 py-4 whitespace-nowrap">سعر الشحنة</th>
    <th className="px-6 py-4 whitespace-nowrap">مبلغ الشحن</th>
    <th className="px-6 py-4 whitespace-nowrap">COD</th>

    <th className="px-6 py-4 whitespace-nowrap">الحالة الرئيسية</th>
    <th className="px-6 py-4 whitespace-nowrap">الحالة الفرعية</th>

    <th className="px-6 py-4 whitespace-nowrap">الوزن</th>
    <th className="px-6 py-4 whitespace-nowrap">المحتوى</th>
    <th className="px-6 py-4 whitespace-nowrap">ملاحظات</th>

    <th className="px-6 py-4 whitespace-nowrap">المندوب / الفرع</th>
    <th className="px-6 py-4 whitespace-nowrap">رقم المندوب / الفرع</th>
    <th className="sticky left-0 z-20 bg-gray-50/95 px-6 py-4 whitespace-nowrap text-left">الإجراءات</th>

  </tr>
</thead>
              <tbody className="divide-y divide-gray-50">
                {shipments.map((shipment) => {
                  const dbStatus = shipmentStatuses.find(
                    (s) => Number(s.id) === Number(shipment.statusId)
                  );

                  const status = statusStyles[shipment.statusId] || {
                    color: 'bg-gray-50 text-gray-700 border-gray-200',
                    icon: Package,
                    label: dbStatus?.nameAr || dbStatus?.name || shipment.statusName || `حالة ${shipment.statusId}`,
                  };

                  const StatusIcon = status.icon;

                  
  

return (
                    <tr key={shipment.id} className="hover:bg-orange-50/30 transition-colors group">

                      {/* تحديد */}
                      <td className="px-4 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={selectedShipmentIds.includes(shipment.id)}
                          onChange={() => toggleShipmentSelection(shipment.id)}
                          className="w-4 h-4 accent-orange-600 cursor-pointer"
                        />
                      </td>

                      {/* رقم التتبع */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => navigate(`/shipments/${shipment.id}`)}
                          className="font-mono font-bold text-orange-600 hover:text-orange-700 hover:underline bg-orange-50 hover:bg-orange-100 px-2.5 py-1.5 rounded-md text-xs transition-colors cursor-pointer"
                          title="عرض تفاصيل الشحنة"
                        >
                          {shipment.trackingNumber || '-'}
                        </button>
                      </td>

                      {/* 2 - تاريخ الإنشاء */}
                      <td className="px-6 py-4 text-gray-500 whitespace-nowrap">
                        {shipment.createdAt
                          ? new Date(shipment.createdAt).toLocaleDateString('ar-EG')
                          : '-'}
                      </td>

                      {/* 3 - الراسل */}
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {shipment.senderName || '-'}
                        </div>
                        {shipment.senderPhone && (
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" />
                            {shipment.senderPhone}
                          </div>
                        )}
                      </td>

                      {/* 4 - محافظة الراسل */}
                      <td className="px-6 py-4 text-gray-600">
                        {shipment.senderCity || '-'}
                      </td>

                      {/* 5 - المرسل إليه */}
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {shipment.receiverName || '-'}
                        </div>
                        {shipment.receiverPhone && (
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" />
                            {shipment.receiverPhone}
                          </div>
                        )}
                      </td>

                      {/* 6 - محافظة المرسل إليه */}
                      <td className="px-6 py-4 text-gray-600">
                        {shipment.receiverCity || '-'}
                      </td>

                      {/* 7 - منطقة المرسل إليه */}
                      <td className="px-6 py-4 text-gray-600">
                        {shipment.receiverArea || '-'}
                      </td>

                      {/* 8 - العنوان التفصيلي */}
                      <td className="px-6 py-4 text-gray-600 min-w-[220px]">
                        <div>{shipment.receiverAddress || '-'}</div>

                        {(shipment.receiverBuildingNumber ||
                          shipment.receiverFloor ||
                          shipment.receiverApartment ||
                          shipment.receiverLandmark) && (
                          <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                            {shipment.receiverBuildingNumber && (
                              <div>عمارة: {shipment.receiverBuildingNumber}</div>
                            )}
                            {shipment.receiverFloor && (
                              <div>الدور: {shipment.receiverFloor}</div>
                            )}
                            {shipment.receiverApartment && (
                              <div>شقة: {shipment.receiverApartment}</div>
                            )}
                            {shipment.receiverLandmark && (
                              <div>علامة مميزة: {shipment.receiverLandmark}</div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* 9 - سعر الشحنة */}
                      <td className="px-6 py-4 font-bold text-gray-900 whitespace-nowrap">
                        {shipment.totalAmount ?? 0} ج.م
                      </td>

                      {/* 10 - مبلغ الشحن */}
                      <td className="px-6 py-4 text-gray-700 whitespace-nowrap">
                        {shipment.shippingCost ?? 0} ج.م
                      </td>

                      {/* 11 - COD */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {shipment.codAmount > 0 ? (
                          <span className="font-bold text-orange-600">
                            {shipment.codAmount} ج.م
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>

                      {/* 12 - الحالة الرئيسية */}
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${status.color}`}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {status.label}
                        </span>
                      </td>

                      {/* 13 - الحالة الفرعية */}
                      <td className="px-6 py-4">
                        {shipment.subStatusNameAr || shipment.subStatusName ? (
                          <span
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border"
                            style={{
                              backgroundColor: shipment.subStatusColor
                                ? `${shipment.subStatusColor}15`
                                : undefined,
                              color: shipment.subStatusColor || undefined,
                              borderColor: shipment.subStatusColor || '#e5e7eb',
                            }}
                          >
                            {shipment.subStatusNameAr || shipment.subStatusName}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>

                      {/* 14 - الوزن */}
                      <td className="px-6 py-4 text-gray-700 whitespace-nowrap">
                        {shipment.weight ?? 0} كجم
                      </td>

                      {/* 15 - محتوى الشحنة */}
                      <td className="px-6 py-4 text-gray-600 min-w-[180px]">
                        {shipment.contentType ||
                          shipment.packageDescription ||
                          shipment.description ||
                          '-'}
                      </td>

                      {/* 16 - ملاحظات */}
                      <td className="px-6 py-4 text-gray-600 min-w-[180px]">
                        {shipment.deliveryNotes || '-'}
                      </td>

                      {/* 17 - المندوب / الفرع */}
                      <td className="px-6 py-4 min-w-[160px]">
                        {shipment.delegateName ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {shipment.delegateName}
                            </div>
                            <div className="text-xs text-gray-500">مندوب</div>
                          </div>
                        ) : shipment.branchName ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {shipment.branchName}
                            </div>
                            <div className="text-xs text-gray-500">فرع</div>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>

                      {/* 18 - رقم المندوب / الفرع */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {shipment.delegateName ? (
                          shipment.delegatePhone ? (
                            <div className="text-gray-600 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {shipment.delegatePhone}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )
                        ) : shipment.branchName ? (
                          shipment.branchPhone ? (
                            <div className="text-gray-600 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {shipment.branchPhone}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>



                      {/* الإجراءات */}
                      <td className="sticky left-0 z-20 bg-white px-4 py-4 text-left whitespace-nowrap shadow-[-4px_0_8px_rgba(0,0,0,0.04)]">
                        <div className="flex items-center justify-start gap-1">
                          <button
                            type="button"
                            onClick={() => navigate(`/shipments/${shipment.id}`)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="عرض التفاصيل"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {canManage && (
                            <>
                              <button
                                type="button"
                                onClick={() => navigate(`/shipments/${shipment.id}/edit`)}
                                className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                title="تعديل"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(shipment.id)}
                                className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                title="حذف"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && shipments.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-50/50">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-500">
                عرض <span className="font-bold text-gray-900">{shipments.length}</span> من أصل <span className="font-bold text-gray-900">{totalItems}</span> شحنة
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">عرض:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
                <span className="text-sm text-gray-600">شحنة</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
                السابق
              </button>
              <span className="px-3 py-2 text-sm font-bold text-gray-900 bg-white border border-gray-200 rounded-lg">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                التالي
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
